import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Popover,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import { SvgIconProps } from '@material-ui/core/SvgIcon'
import AddIcon from '@material-ui/icons/Add'
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined'
import BuildIcon from '@material-ui/icons/Build'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline'
import CloseIcon from '@material-ui/icons/Close'
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined'
import EventAvailableIcon from '@material-ui/icons/EventAvailable'
import EventBusyIcon from '@material-ui/icons/EventBusy'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import NotesIcon from '@material-ui/icons/Notes'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import PetsIcon from '@material-ui/icons/Pets'
import PhoneIcon from '@material-ui/icons/Phone'
import TuneIcon from '@material-ui/icons/Tune'
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router-dom'

import { RenewalOffer, RenewalSequence } from '../../../models/Concession'
import { LeasePet } from '../../../models/Lease/types'
import { formatPetLabel } from '../../../models/Lease/services'
import {
    EnrichedValue,
    Lead,
    LeadLeasePackage,
    LeadTour,
    LeadTourStatus,
    Prospect,
} from '../../../models/Outreach/types'
import { axiosInstance } from '../../../helpers'
import { RenewalOfferPanel } from '../Renewals/RenewalOfferPanel'
import {
    useLeadAuditTrail,
    useLeadNotes,
} from '../../../hooks/Outreach/useLeadCrm'
import { useVoiceAgentCallList } from '../../../hooks/useVoiceAgentCalls'
import {
    PersonServiceRequest,
    PersonServiceRequestGroup,
    PersonServiceRequestWorkorder,
    usePersonServiceRequests,
} from '../../../hooks/usePersonServiceRequests'
import { LeadMessagesSection } from './LeadMessagesSection'
import { EditPetsDialog } from './EditPetsDialog'
import { EditPreferencesDialog } from './EditPreferencesDialog'
import {
    canResendScreeningInvitation,
    canStartScreening,
    getBackgroundCheckDisplay,
    getScreeningResultDisplay,
} from '../../../utils/screeningStatus'
import { NextActionLine } from '../components/NextActionMark'
import { DEFAULT_PIPELINE_TABS, PipelineTab } from './pipelineTabs'
import { recordsLeadPath, recordsPersonPath } from '../../Records/paths'

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        width: 720,
        maxWidth: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    header: {
        padding: theme.spacing(2.5, 2.5, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
    },
    topRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
    },
    avatar: {
        width: 44,
        height: 44,
        borderRadius: 12,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: '0.95rem',
        flexShrink: 0,
    },
    identity: {
        minWidth: 0,
        flex: 1,
    },
    nameRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    name: {
        fontWeight: 700,
        fontSize: '1.15rem',
        lineHeight: 1.25,
    },
    contact: {
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        marginTop: 2,
        wordBreak: 'break-word',
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
        marginTop: theme.spacing(1),
    },
    headerTools: {
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexShrink: 0,
    },
    auditLink: {
        textTransform: 'none',
        fontSize: '0.75rem',
        minWidth: 0,
        padding: theme.spacing(0.5, 0.75),
        color: theme.palette.text.secondary,
    },
    deleteLink: {
        textTransform: 'none',
        fontSize: '0.75rem',
        minWidth: 0,
        padding: theme.spacing(0.5, 0.75),
        color: '#b91c1c',
    },
    nextActionBlock: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
    },
    stageWrap: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
    },
    moveStage: {
        border: 'none',
        background: 'none',
        padding: theme.spacing(0, 0.5),
        color: theme.palette.text.secondary,
        fontSize: '0.7rem',
        fontWeight: 600,
        cursor: 'default',
        opacity: 0.7,
    },
    body: {
        flex: 1,
        overflowY: 'auto',
        padding: theme.spacing(2, 2.5, 2.5),
    },
    section: {
        marginBottom: theme.spacing(1.5),
    },
    sectionCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        background: theme.palette.background.paper,
        padding: theme.spacing(1.25, 1.5, 1.5),
    },
    sectionCardMessages: {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
    },
    sectionCardNotes: {
        borderLeft: `4px solid #f59e0b`,
    },
    sectionCardDocuments: {
        borderLeft: '4px solid #64748b',
    },
    sectionBeforeStory: {
        marginBottom: theme.spacing(2.5),
    },
    sectionTitle: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    sectionTitleIcon: {
        fontSize: 16,
        flexShrink: 0,
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    sectionHeaderTitle: {
        marginBottom: 0,
    },
    addNoteButton: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.75rem',
        lineHeight: 1.2,
        minWidth: 0,
        minHeight: 0,
        padding: '4px 10px 4px 6px',
        borderRadius: 16,
        '& .MuiButton-startIcon': {
            marginRight: 4,
            marginLeft: 0,
        },
        '& .MuiButton-startIcon > *:first-child': {
            fontSize: 16,
        },
    },
    notesList: {
        maxHeight: 168,
        overflowY: 'auto',
    },
    noteComposePaper: {
        width: 360,
        maxWidth: 'calc(100vw - 32px)',
        padding: theme.spacing(1.5),
        borderRadius: 10,
    },
    storyToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        width: '100%',
        textAlign: 'left',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        background: theme.palette.background.paper,
        padding: theme.spacing(1.25, 1.5),
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    storyToggleOpen: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderBottomColor: 'transparent',
    },
    storyTitle: {
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        width: 110,
        flexShrink: 0,
    },
    storyTitleIcon: {
        fontSize: 16,
        flexShrink: 0,
    },
    storySummary: {
        flex: 1,
        minWidth: 0,
        fontSize: '0.8125rem',
        color: theme.palette.text.primary,
    },
    storyChevron: {
        color: theme.palette.text.secondary,
        fontSize: 20,
        transition: 'transform 120ms ease',
    },
    storyChevronOpen: {
        transform: 'rotate(180deg)',
    },
    storyBody: {
        border: `1px solid ${theme.palette.divider}`,
        borderTop: 'none',
        borderBottomLeftRadius: 8,
        borderBottomRightRadius: 8,
        padding: theme.spacing(1.5, 1.5, 1.25),
        marginBottom: theme.spacing(1),
    },
    leaseGroup: {
        marginTop: theme.spacing(1.5),
        '&:first-child': {
            marginTop: 0,
        },
    },
    leaseGroupTitle: {
        fontSize: '0.75rem',
        fontWeight: 700,
        marginBottom: theme.spacing(0.75),
    },
    requestFact: {
        marginBottom: theme.spacing(1),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    requestFactTitle: {
        fontSize: '0.8125rem',
        fontWeight: 600,
    },
    requestFactMeta: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
    },
    storyActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    nestedToggle: {
        display: 'block',
        width: '100%',
        textAlign: 'left',
        border: 'none',
        background: 'none',
        padding: theme.spacing(0.5, 0),
        cursor: 'pointer',
        color: theme.palette.primary.main,
        fontSize: '0.8125rem',
        fontWeight: 600,
    },
    row: {
        display: 'flex',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(0.75),
    },
    label: {
        width: 140,
        flexShrink: 0,
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        fontWeight: 600,
    },
    value: {
        flex: 1,
        fontSize: '0.8125rem',
        wordBreak: 'break-word',
    },
    empty: {
        color: theme.palette.text.disabled,
        fontSize: '0.8125rem',
    },
    documentRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(0.75),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    documentName: {
        flex: 1,
        minWidth: 0,
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
    },
    documentMeta: {
        flexShrink: 0,
        fontSize: '0.8125rem',
        color: theme.palette.text.primary,
    },
    noteCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: theme.spacing(1.25),
        marginBottom: theme.spacing(1),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    petBlock: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    petPhoto: {
        width: 72,
        height: 72,
        objectFit: 'cover',
        borderRadius: 8,
        flexShrink: 0,
        backgroundColor: theme.palette.grey[200],
    },
    petFields: {
        flex: 1,
    },
    tourBlock: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    tourIcon: {
        width: 36,
        height: 36,
        borderRadius: 8,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(6, 182, 212, 0.12)',
        color: '#0e7490',
        '& svg': {
            fontSize: 20,
        },
    },
    tourFields: {
        flex: 1,
        minWidth: 0,
    },
    tourStatus: {
        fontSize: '0.8125rem',
        fontWeight: 700,
        marginBottom: 4,
    },
    noteMeta: {
        fontSize: '0.7rem',
        color: theme.palette.text.secondary,
        marginBottom: 4,
    },
    noteMetaRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
        marginBottom: 4,
    },
    noteSourceChip: {
        height: 18,
        fontSize: '0.625rem',
        fontWeight: 700,
        letterSpacing: '0.03em',
    },
    auditRow: {
        display: 'grid',
        gridTemplateColumns: '118px 1fr',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 0),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    auditTime: {
        fontSize: '0.7rem',
        color: theme.palette.text.secondary,
    },
}))

interface PersonFileDialogProps {
    open: boolean
    lead: Lead | null
    prospect?: Prospect | null
    userId?: number | null
    loading?: boolean
    onClose: () => void
    readOnly?: boolean
    renewalOffer?: RenewalOffer | null
    sequences?: RenewalSequence[]
    pathTabs?: PipelineTab[]
    pathStage?: string
    onChangeSequence?: (offerId: number, sequence: number | null) => void
    isChangingSequence?: boolean
    onInviteToApply?: (leadId: number) => Promise<unknown>
    onStartScreening?: (leadId: number) => Promise<unknown>
    onResendScreeningInvitation?: (leadId: number) => Promise<unknown>
    onSendLeaseOffer?: (lead: Lead) => void
    onSendLeasePackage?: (leadId: number) => Promise<unknown>
    onScheduleTour?: (leadId: number, tourId?: number) => void
    isStartingScreening?: boolean
    isResendingScreeningInvitation?: boolean
    isSendingLeasePackage?: boolean
    onDeleteLead?: (leadId: number) => Promise<unknown>
    isDeletingLead?: boolean
}

type StoryKey =
    | 'housing'
    | 'pets'
    | 'application'
    | 'screening'
    | 'tours'
    | 'calls'
    | 'serviceRequests'

const STORY_ACCENT: Record<StoryKey, string> = {
    housing: '#3b82f6',
    pets: '#ec4899',
    application: '#6366f1',
    screening: '#10b981',
    tours: '#06b6d4',
    calls: '#8b5cf6',
    serviceRequests: '#f97316',
}

const CARD_ACCENT = {
    notes: '#f59e0b',
    documents: '#64748b',
}

function formatPersonPhone(raw: string): string {
    const digits = raw.replace(/\D/g, '')
    if (digits.length === 11 && digits.startsWith('1')) {
        return `+1 (${digits.slice(1, 4)})-${digits.slice(4, 7)}-${digits.slice(
            7,
        )}`
    }
    if (digits.length === 10) {
        return `+1 (${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(
            6,
        )}`
    }
    return raw
}

function resolvePrimaryAction({
    label,
    lead,
    canInvite,
    canScreen,
    canResend,
    onInviteToApply,
    onStartScreening,
    onResendScreeningInvitation,
    onSendLeaseOffer,
    onSendLeasePackage,
    isStartingScreening,
    isResendingScreeningInvitation,
    isSendingLeasePackage,
}: {
    label: string
    lead: Lead
    canInvite: boolean
    canScreen: boolean
    canResend: boolean
    onInviteToApply?: (leadId: number) => Promise<unknown>
    onStartScreening?: (leadId: number) => Promise<unknown>
    onResendScreeningInvitation?: (leadId: number) => Promise<unknown>
    onSendLeaseOffer?: (lead: Lead) => void
    onSendLeasePackage?: (leadId: number) => Promise<unknown>
    isStartingScreening: boolean
    isResendingScreeningInvitation: boolean
    isSendingLeasePackage: boolean
}): { label: string; disabled?: boolean; onClick: () => void } | null {
    if (label === 'Invite to apply' && canInvite && onInviteToApply) {
        return { label, onClick: () => onInviteToApply(lead.id) }
    }
    if (label === 'Send screening' && canScreen && onStartScreening) {
        return {
            label,
            disabled: isStartingScreening,
            onClick: () => onStartScreening(lead.id),
        }
    }
    if (
        label === 'Resend screening' &&
        canResend &&
        onResendScreeningInvitation
    ) {
        return {
            label,
            disabled: isResendingScreeningInvitation,
            onClick: () => onResendScreeningInvitation(lead.id),
        }
    }
    if (label === 'Send lease offer' && onSendLeaseOffer) {
        return { label, onClick: () => onSendLeaseOffer(lead) }
    }
    if (label === 'Send lease packet' && onSendLeasePackage) {
        return {
            label,
            disabled: isSendingLeasePackage,
            onClick: () => onSendLeasePackage(lead.id),
        }
    }
    return null
}

function documentStatusLabel(status: string | null | undefined): string | null {
    if (!status) return null
    const normalized = status.toLowerCase()
    if (normalized === 'complete' || normalized === 'completed') return 'Signed'
    if (normalized === 'sent') return 'Sent'
    if (normalized === 'pending') return 'Pending'
    return status
}

const DOC_TYPE_LABELS: Record<string, string> = {
    APPLICATION: 'Application',
    SELF_GUARANTOR: 'Self guarantor',
    GUARANTOR_APPLICATION: 'Guarantor application',
    GUARANTOR_AGREEMENT: 'Guarantor agreement',
    LEASE: 'Lease',
}

function humanizeTemplateName(name: string | null | undefined): string {
    if (!name) return ''
    const stripped = name
        .replace(/\.pdf$/i, '')
        .replace(/[-_]?blank$/i, '')
        .replace(/^\d+[-_]+/, '')
        .replace(/[-_]+/g, ' ')
        .trim()
    if (!stripped) return ''
    return stripped.replace(/\b\w/g, (char) => char.toUpperCase())
}

type LeasePackageDoc = LeadLeasePackage['documents'][number]

function leasePackageDocTitle(
    doc: LeasePackageDoc,
    typeCounts: Record<string, number>,
): string {
    const type = doc.document_type || ''
    const label =
        doc.document_type_label || DOC_TYPE_LABELS[type] || 'Lease document'
    if ((typeCounts[type] || 0) <= 1) {
        return label
    }
    const extra = humanizeTemplateName(doc.signable_doc_name)
    if (!extra || extra.toLowerCase() === label.toLowerCase()) {
        return label
    }
    return `${label} (${extra})`
}

function collectPersonDocuments(
    lead: Lead,
): Array<{
    key: string
    name: string
    status: string | null
    at: string | null
    onOpen?: () => void
    href?: string
}> {
    const rows: Array<{
        key: string
        name: string
        status: string | null
        at: string | null
        onOpen?: () => void
        href?: string
    }> = []
    if (lead.has_submission_pdf) {
        rows.push({
            key: 'application-pdf',
            name: 'Application PDF',
            status: null,
            at: null,
        })
    }
    const signedApp = lead.application_signing?.signed_pdf_download_url
    if (signedApp) {
        rows.push({
            key: 'signed-application',
            name: 'Signed application',
            status: 'Signed',
            at: lead.application_signing?.completed_at ?? null,
            href: signedApp,
        })
    }
    if (lead.screening_report_url) {
        rows.push({
            key: 'screening-report',
            name: 'Screening report',
            status: null,
            at: null,
            href: lead.screening_report_url,
        })
    }
    const leaseDocs = lead.lease_package?.documents ?? []
    const typeCounts = leaseDocs.reduce<Record<string, number>>(
        (counts, doc) => {
            const type = doc.document_type || ''
            counts[type] = (counts[type] || 0) + 1
            return counts
        },
        {},
    )
    for (const doc of leaseDocs) {
        rows.push({
            key: `lease-${doc.signing_request_id}`,
            name: leasePackageDocTitle(doc, typeCounts),
            status: documentStatusLabel(doc.status),
            at: doc.completed_at || doc.sent_at,
            href: doc.signed_pdf_url || undefined,
        })
    }
    return rows
}

function initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) return '?'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

function formatMoney(value: number | null | undefined): string | null {
    if (value == null) return null
    return value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    })
}

function formatDate(value: string | null | undefined): string | null {
    if (!value) return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

function formatDateTime(value: string | null | undefined): string | null {
    if (!value) return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })
}

function tourStatusLabel(status: string | null | undefined): string {
    switch (status) {
        case 'scheduled':
            return 'Scheduled'
        case 'completed':
            return 'Completed'
        case 'no_show':
            return 'No show'
        case 'skipped':
            return 'Skipped'
        default:
            return status || ''
    }
}

function tourStatusMeta(
    status: LeadTourStatus | string | null | undefined,
): {
    Icon: typeof CalendarTodayIcon
    color: string
} {
    switch (status) {
        case 'completed':
            return { Icon: EventAvailableIcon, color: '#047857' }
        case 'no_show':
            return { Icon: EventBusyIcon, color: '#b45309' }
        case 'skipped':
            return { Icon: CalendarTodayIcon, color: '#64748b' }
        default:
            return { Icon: CalendarTodayIcon, color: '#0e7490' }
    }
}

function petShortLabel(pet: LeasePet): string {
    const name = (pet.name || '').trim()
    const rawType = (pet.animal_type || '').trim()
    const typeLabel = rawType
        ? rawType.charAt(0).toUpperCase() + rawType.slice(1)
        : ''
    if (name && typeLabel) {
        return `${typeLabel} ${name}`
    }
    return name || typeLabel || 'Pet'
}

function petsSummary(pets: LeasePet[] | undefined): string {
    if (!pets || pets.length === 0) {
        return 'No pets'
    }
    if (pets.length > 3) {
        return `${pets.length} pets`
    }
    return pets.map(petShortLabel).join(', ')
}

function serviceRequestStatusLabel(status: string | null | undefined): string {
    if (!status) return 'Submitted'
    const labels: Record<string, string> = {
        SUBMITTED: 'Submitted',
        ACKNOWLEDGED: 'Acknowledged',
        IN_PROGRESS: 'In progress',
        COMPLETED: 'Completed',
    }
    return labels[status] || status
}

function serviceRequestCategoryLabel(
    category: string | null | undefined,
): string | null {
    if (!category) return null
    const labels: Record<string, string> = {
        PLUMBING: 'Plumbing',
        ELECTRICAL: 'Electrical',
        HVAC: 'HVAC',
        APPLIANCES: 'Appliances',
        HARDWARE_LOCKS: 'Hardware / locks',
        FLOORING: 'Flooring',
        PAINTING: 'Painting',
        OTHER: 'Other',
    }
    return labels[category] || category
}

function serviceRequestPriorityLabel(
    priority: string | null | undefined,
): string | null {
    if (!priority) return null
    const labels: Record<string, string> = {
        LOW: 'Low',
        MEDIUM: 'Medium',
        HIGH: 'High',
        EMERGENCY: 'Emergency',
    }
    return labels[priority] || priority
}

function workorderStatusLabel(
    status: string | null | undefined,
): string | null {
    if (!status) return null
    const labels: Record<string, string> = {
        ASSIGNED: 'Assigned',
        IN_PROGRESS: 'In progress',
        PAUSE: 'Paused',
        COMPLETE: 'Complete',
        GO_BACK: 'Sent back',
        PRE_APPROVED: 'Pre-approved',
        APPROVED: 'Approved',
        INVOICED: 'Invoiced',
        INVOICED_OUT: 'Invoiced out',
        INVOICE_REQUEST: 'Invoice requested',
    }
    return labels[status] || status
}

function serviceRequestWorkorderLabel(
    workorder?: PersonServiceRequestWorkorder | null,
): string | null {
    if (!workorder) return null
    const assigned = workorder.assigned_to?.name?.trim()
    const status = workorderStatusLabel(workorder.status)
    if (assigned && status) {
        return `${status} · ${assigned}`
    }
    return assigned || status
}

function serviceRequestFacts(row: PersonServiceRequest): string {
    return joinParts([
        serviceRequestCategoryLabel(row.category),
        serviceRequestPriorityLabel(row.priority),
        serviceRequestStatusLabel(row.status),
        serviceRequestWorkorderLabel(row.workorder),
        formatDate(row.created_date),
    ])
}

function serviceRequestsSummary(
    requests: Array<{ status: string }>,
    loaded: boolean,
    hasResident: boolean,
): string {
    if (!hasResident) {
        return 'No service requests'
    }
    if (!loaded) {
        return 'Service requests'
    }
    if (requests.length === 0) {
        return 'No service requests'
    }
    const openCount = requests.filter((row) => row.status !== 'COMPLETED')
        .length
    const requestLabel = `${requests.length} request${
        requests.length === 1 ? '' : 's'
    }`
    if (openCount === 0) {
        return requestLabel
    }
    return `${requestLabel} · ${openCount} open`
}

function qualifyingLabel(method: string | null | undefined): string | null {
    if (!method) return null
    const labels: Record<string, string> = {
        personal_guarantor: 'Personal guarantor',
        self_qualified: 'Self-qualified',
        leap_gwp: 'LEAP / GWP',
    }
    return labels[method] || method.replace(/_/g, ' ')
}

function currentPathIndex(stage: string, tabs: PipelineTab[]): number {
    if (stage === 'app_sent' || stage === 'app_signed_pending_review') {
        return tabs.findIndex((tab) => tab.step_type === 'application')
    }
    if (stage === 'lease_sent' || stage === 'lease_signed') {
        return tabs.findIndex((tab) => tab.step_type === 'lease')
    }
    const byId = tabs.findIndex(
        (tab) => tab.id === stage || tab.stage === stage,
    )
    if (byId >= 0) return byId
    return tabs.findIndex((tab) => tab.step_type === stage)
}

function contactValue(
    value: string | EnrichedValue | null | undefined,
): string {
    if (!value) return ''
    if (typeof value === 'string') return value
    return value.value || ''
}

function joinParts(parts: Array<string | null | undefined>): string {
    return parts.filter((part): part is string => Boolean(part)).join(' · ')
}

function realLabel(label: string | null | undefined): string | null {
    if (!label || label === '—') return null
    return label
}

export const PersonFileDialog: React.FC<PersonFileDialogProps> = ({
    open,
    lead,
    prospect = null,
    userId = null,
    loading = false,
    onClose,
    readOnly = false,
    onInviteToApply,
    onStartScreening,
    onResendScreeningInvitation,
    onSendLeaseOffer,
    onSendLeasePackage,
    onScheduleTour,
    isStartingScreening = false,
    isResendingScreeningInvitation = false,
    isSendingLeasePackage = false,
    onDeleteLead,
    isDeletingLead = false,
    renewalOffer = null,
    sequences = [],
    pathTabs,
    pathStage,
    onChangeSequence,
    isChangingSequence = false,
}) => {
    const classes = useStyles()
    const history = useHistory()
    const [mode, setMode] = useState<'person' | 'audit'>('person')
    const [noteBody, setNoteBody] = useState('')
    const [
        noteComposeAnchor,
        setNoteComposeAnchor,
    ] = useState<HTMLElement | null>(null)
    const [preferencesOpen, setPreferencesOpen] = useState(false)
    const [petsOpen, setPetsOpen] = useState(false)
    const [petsStartWithNew, setPetsStartWithNew] = useState(false)
    const [customQuestionsOpen, setCustomQuestionsOpen] = useState(false)
    const [openSections, setOpenSections] = useState<
        Partial<Record<StoryKey, boolean>>
    >({})
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

    const leadId = lead?.id ?? null
    const hasAccount = Boolean(lead?.user_id)
    const callsOpen = Boolean(openSections.calls)
    const serviceUserId =
        lead?.user_id ?? userId ?? renewalOffer?.tenant ?? null
    const hasResident = Boolean(serviceUserId)
    const { notes, isLoading: notesLoading, addNote, isSaving } = useLeadNotes(
        open ? leadId : null,
    )
    const { events, isLoading: auditLoading } = useLeadAuditTrail(
        leadId,
        open && mode === 'audit',
    )
    const {
        calls,
        count: callsCount,
        isLoading: callsLoading,
    } = useVoiceAgentCallList(1, 25, {
        lead_id: open && leadId ? leadId : -1,
    })
    const {
        requests: serviceRequests,
        groups: serviceRequestGroups,
        isLoading: serviceRequestsLoading,
    } = usePersonServiceRequests(serviceUserId, open && hasResident)

    useEffect(() => {
        setMode('person')
        setCustomQuestionsOpen(false)
        setPreferencesOpen(false)
        setPetsOpen(false)
        setPetsStartWithNew(false)
        setOpenSections({})
        setNoteBody('')
        setNoteComposeAnchor(null)
        setDeleteConfirmOpen(false)
    }, [leadId, open])

    if (!open) {
        return null
    }

    if (loading && !lead && !renewalOffer && !prospect) {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                classes={{ paper: classes.paper }}
            >
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight={240}
                >
                    <CircularProgress size={28} />
                </Box>
            </Dialog>
        )
    }

    if (!lead && !renewalOffer && !prospect) {
        return null
    }

    const name =
        lead?.identity?.name ||
        lead?.prospect_detail?.name ||
        prospect?.name ||
        renewalOffer?.tenant_name ||
        'Unknown'
    const email =
        lead?.identity?.email ||
        lead?.prospect_detail?.email ||
        contactValue(prospect?.email) ||
        renewalOffer?.tenant_email ||
        ''
    const phone =
        lead?.identity?.phone ||
        (typeof lead?.prospect_detail?.phone === 'string'
            ? lead.prospect_detail.phone
            : '') ||
        contactValue(prospect?.phone) ||
        ''
    const tabs = pathTabs ?? DEFAULT_PIPELINE_TABS
    const pathIndex = pathStage
        ? tabs.findIndex((tab) => tab.id === pathStage)
        : lead
        ? currentPathIndex(lead.pipeline_stage, tabs)
        : -1
    const pipelineStageLabel =
        pathIndex >= 0 ? tabs[pathIndex].label : pathStage || null
    const prospectStageLabel =
        !lead && prospect ? (email || phone ? 'Prospect' : 'Target') : null
    const stageLabel = pipelineStageLabel || prospectStageLabel
    const waiting = lead?.waiting_on
        ? lead.waiting_on
        : !lead && prospect
        ? {
              waiting_on: 'us' as const,
              label: email || phone ? 'Invite to apply' : 'Reveal contact',
              detail: null,
          }
        : null
    const isRenewal = Boolean(renewalOffer)
    const canInvite = Boolean(
        lead &&
            !isRenewal &&
            onInviteToApply &&
            lead.account_status !== 'active',
    )
    const canScreen = Boolean(
        lead &&
            canStartScreening({
                pipelineStage: lead.pipeline_stage,
                screeningStatus: lead.screening_request?.status,
                screeningResult: lead.screening_result,
            }),
    )
    const canResend = Boolean(
        lead &&
            canResendScreeningInvitation({
                pipelineStage: lead.pipeline_stage,
                screeningStatus: lead.screening_request?.status,
                invitationUrl: lead.screening_request?.invitation_url,
                candidateId: lead.screening_request?.checkr_candidate_id,
            }),
    )
    const screening = lead
        ? getScreeningResultDisplay(lead.screening_result)
        : null
    const background = lead
        ? getBackgroundCheckDisplay(lead.background_check)
        : null
    const screeningLabel = realLabel(screening?.label)
    const backgroundLabel = realLabel(background?.label)

    const floorPlan = lead?.preferred_unit_config?.name || null
    const budget = formatMoney(lead?.preferred_rent_cost)
    const moveIn = formatDate(lead?.preferred_move_in_date)
    const moveOut = formatDate(lead?.preferred_move_out_date)
    const school = lead?.school_attendance_name || null
    const premiums = lead?.preferred_premiums?.length
        ? lead.preferred_premiums.map((row) => row.name).join(', ')
        : null
    const roommates = lead?.preferred_roommates?.length
        ? lead.preferred_roommates.map((row) => row.name).join(', ')
        : null
    const requests = lead?.additional_requests || null
    const customAnswers = lead?.custom_answers ?? []
    const housingSummary = joinParts([
        floorPlan,
        budget,
        moveIn ? `Move-in ${moveIn}` : null,
    ])
    const hasHousing =
        Boolean(housingSummary) ||
        Boolean(moveOut || school || premiums || roommates || requests) ||
        customAnswers.length > 0

    const qualifying = qualifyingLabel(lead?.qualifying_method)
    const progress = lead?.workbook_sections_total
        ? `${lead.workbook_sections_complete ?? 0}/${
              lead.workbook_sections_total
          } sections`
        : null
    const guarantor = lead?.guarantor
        ? [lead.guarantor.name, lead.guarantor.email]
              .filter(Boolean)
              .join(' · ')
        : lead?.qualifying_method === 'personal_guarantor'
        ? 'Personal guarantor'
        : null
    const petRows = lead?.pets ?? []
    const applicationSummary = joinParts([qualifying, progress])
    const hasApplication =
        Boolean(applicationSummary) ||
        Boolean(guarantor || lead?.has_submission_pdf)

    const screeningSummary = joinParts([screeningLabel, backgroundLabel])
    const hasScreening =
        Boolean(screeningSummary) ||
        lead?.credit_score != null ||
        Boolean(lead?.screening_report_url)

    const callsSummary =
        callsCount > 0
            ? joinParts([
                  `${callsCount} call${callsCount === 1 ? '' : 's'}`,
                  formatDateTime(calls[0]?.created_at),
              ])
            : callsLoading
            ? ''
            : 'No calls'

    const tourSummary = lead?.current_tour
        ? joinParts([
              tourStatusLabel(lead.current_tour.status),
              formatDateTime(lead.current_tour.starts_at),
              lead.current_tour.given_by?.name,
          ])
        : null
    const currentTour = lead?.current_tour ?? null
    const otherTours = (lead?.tours ?? []).filter(
        (tour) => tour.id !== currentTour?.id,
    )

    const toggleSection = (key: StoryKey) => {
        setOpenSections((current) => ({
            ...current,
            [key]: !current[key],
        }))
    }

    const closeNoteCompose = () => {
        setNoteComposeAnchor(null)
        setNoteBody('')
    }

    const handleAddNote = async () => {
        if (!noteBody.trim()) return
        await addNote(noteBody.trim())
        closeNoteCompose()
    }

    const handleOpenApplicationPdf = async () => {
        if (!lead) return
        try {
            const { data } = await axiosInstance.get<{
                download_url: string
            }>(`outreach/leads/${lead.id}/application/submission-pdf/`)
            if (data.download_url) {
                window.open(data.download_url, '_blank', 'noopener,noreferrer')
            } else {
                toast.error('Submission PDF not available.')
            }
        } catch {
            toast.error('Unable to download application PDF.')
        }
    }

    const waitingOnUs = waiting?.waiting_on === 'us'
    const nextActionLabel = waiting?.label || ''
    const primaryAction =
        !readOnly && waitingOnUs && lead && nextActionLabel
            ? resolvePrimaryAction({
                  label: nextActionLabel,
                  lead,
                  canInvite,
                  canScreen,
                  canResend,
                  onInviteToApply,
                  onStartScreening,
                  onResendScreeningInvitation,
                  onSendLeaseOffer,
                  onSendLeasePackage,
                  isStartingScreening,
                  isResendingScreeningInvitation,
                  isSendingLeasePackage,
              })
            : null
    const documents = lead ? collectPersonDocuments(lead) : []
    const canScheduleTour = Boolean(
        lead && !readOnly && !isRenewal && onScheduleTour,
    )

    const deleteIsProtected =
        lead != null &&
        (lead.pipeline_stage === 'future_resident' ||
            lead.pipeline_stage === 'lease_sent' ||
            lead.pipeline_stage === 'lease_signed' ||
            Boolean(lead.lease_package) ||
            Boolean(lead.user_id) ||
            lead.account_status === 'active')

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                classes={{ paper: classes.paper }}
            >
                <Box className={classes.header}>
                    <Box className={classes.topRow}>
                        <Box className={classes.avatar}>{initials(name)}</Box>
                        <Box className={classes.identity}>
                            <Box className={classes.nameRow}>
                                <Typography className={classes.name}>
                                    {name}
                                </Typography>
                                {stageLabel && (
                                    <Box className={classes.stageWrap}>
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={stageLabel}
                                        />
                                        {lead && (
                                            <Tooltip title="Move this person to another stage. Coming soon.">
                                                <span>
                                                    <button
                                                        type="button"
                                                        className={
                                                            classes.moveStage
                                                        }
                                                        disabled
                                                    >
                                                        Move
                                                    </button>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Box>
                                )}
                            </Box>
                            <Typography className={classes.contact}>
                                {email || 'No email'}
                                {phone ? ` · ${formatPersonPhone(phone)}` : ''}
                            </Typography>
                            {renewalOffer && (
                                <Box className={classes.chips}>
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label="Renewal"
                                    />
                                </Box>
                            )}
                        </Box>
                        <Box className={classes.headerTools}>
                            {serviceUserId != null || lead?.id != null ? (
                                <Button
                                    className={classes.auditLink}
                                    startIcon={
                                        <OpenInNewIcon
                                            style={{ fontSize: 14 }}
                                        />
                                    }
                                    onClick={() => {
                                        if (serviceUserId != null) {
                                            history.push(
                                                recordsPersonPath(
                                                    serviceUserId,
                                                ),
                                            )
                                        } else if (lead?.id != null) {
                                            history.push(
                                                recordsLeadPath(lead.id),
                                            )
                                        }
                                        onClose()
                                    }}
                                >
                                    Records
                                </Button>
                            ) : null}
                            {lead && !readOnly && onDeleteLead && (
                                <Button
                                    className={classes.deleteLink}
                                    disabled={isDeletingLead}
                                    onClick={() => setDeleteConfirmOpen(true)}
                                >
                                    Delete
                                </Button>
                            )}
                            {lead && (
                                <Button
                                    className={classes.auditLink}
                                    onClick={() =>
                                        setMode((current) =>
                                            current === 'person'
                                                ? 'audit'
                                                : 'person',
                                        )
                                    }
                                >
                                    {mode === 'person' ? 'Audit trail' : 'Back'}
                                </Button>
                            )}
                            <IconButton size="small" onClick={onClose}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                    <Box className={classes.nextActionBlock}>
                        <NextActionLine action={waiting} />
                        {primaryAction && mode === 'person' && (
                            <Button
                                size="small"
                                color="primary"
                                variant="contained"
                                disabled={primaryAction.disabled}
                                onClick={primaryAction.onClick}
                            >
                                {primaryAction.label}
                            </Button>
                        )}
                    </Box>
                </Box>

                <Box className={classes.body}>
                    {mode === 'audit' ? (
                        <Box>
                            <Typography className={classes.sectionTitle}>
                                Audit trail
                            </Typography>
                            {auditLoading ? (
                                <CircularProgress size={22} />
                            ) : events.length === 0 ? (
                                <Typography className={classes.empty}>
                                    No activity yet.
                                </Typography>
                            ) : (
                                events.map((event, index) => (
                                    <Box
                                        key={`${event.kind}-${event.at}-${index}`}
                                        className={classes.auditRow}
                                    >
                                        <Typography
                                            className={classes.auditTime}
                                        >
                                            {formatDateTime(event.at)}
                                        </Typography>
                                        <Box>
                                            <Typography
                                                className={classes.value}
                                            >
                                                {event.title}
                                            </Typography>
                                            {event.detail ? (
                                                <Typography
                                                    className={classes.empty}
                                                >
                                                    {event.detail}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    </Box>
                                ))
                            )}
                        </Box>
                    ) : (
                        <>
                            {renewalOffer && (
                                <RenewalOfferPanel
                                    offer={renewalOffer}
                                    sequences={sequences}
                                    readOnly={readOnly}
                                    onChangeSequence={onChangeSequence}
                                    isChangingSequence={isChangingSequence}
                                />
                            )}
                            {prospect && !lead && (
                                <Box className={classes.section}>
                                    <Field
                                        label="School"
                                        value={
                                            prospect.school
                                                ?.effective_display_name
                                        }
                                    />
                                    <Field
                                        label="Campaign"
                                        value={prospect.active_campaign?.name}
                                    />
                                    <Typography className={classes.empty}>
                                        This person is not in the leasing
                                        pipeline yet.
                                    </Typography>
                                    {!readOnly && email && (
                                        <Button
                                            size="small"
                                            color="primary"
                                            href={`mailto:${email}`}
                                            style={{ marginTop: 8 }}
                                        >
                                            Email
                                        </Button>
                                    )}
                                </Box>
                            )}
                            {prospect && !lead && (
                                <StorySection
                                    title="Pets"
                                    icon={PetsIcon}
                                    summary="No pets"
                                    accent={STORY_ACCENT.pets}
                                    open={Boolean(openSections.pets)}
                                    onToggle={() => toggleSection('pets')}
                                >
                                    <Typography className={classes.empty}>
                                        No pets on file.
                                    </Typography>
                                </StorySection>
                            )}
                            {(renewalOffer && !lead) || (prospect && !lead) ? (
                                <PersonServiceRequestSection
                                    groups={serviceRequestGroups}
                                    requests={serviceRequests}
                                    loading={serviceRequestsLoading}
                                    hasResident={hasResident}
                                    open={Boolean(openSections.serviceRequests)}
                                    onToggle={() =>
                                        toggleSection('serviceRequests')
                                    }
                                />
                            ) : null}
                            {lead && (
                                <>
                                    <Box
                                        className={`${classes.section} ${classes.sectionCard} ${classes.sectionCardMessages}`}
                                    >
                                        {hasAccount && lead.user_id ? (
                                            <LeadMessagesSection
                                                leadId={lead.id}
                                                tenantId={lead.user_id}
                                                tenantName={name}
                                                readOnly={readOnly}
                                            />
                                        ) : (
                                            <>
                                                <Typography
                                                    className={
                                                        classes.sectionTitle
                                                    }
                                                >
                                                    <ChatBubbleOutlineIcon
                                                        className={
                                                            classes.sectionTitleIcon
                                                        }
                                                        color="primary"
                                                    />
                                                    Messages
                                                </Typography>
                                                <Typography
                                                    className={classes.empty}
                                                >
                                                    Portal messaging needs an
                                                    applicant account.
                                                </Typography>
                                                {!readOnly && email && (
                                                    <Button
                                                        size="small"
                                                        color="primary"
                                                        href={`mailto:${email}`}
                                                        style={{ marginTop: 8 }}
                                                    >
                                                        Email
                                                    </Button>
                                                )}
                                                {!readOnly &&
                                                    canInvite &&
                                                    nextActionLabel !==
                                                        'Invite to apply' && (
                                                        <Button
                                                            size="small"
                                                            color="primary"
                                                            onClick={() =>
                                                                onInviteToApply?.(
                                                                    lead.id,
                                                                )
                                                            }
                                                            style={{
                                                                marginTop: 8,
                                                            }}
                                                        >
                                                            Invite to apply
                                                        </Button>
                                                    )}
                                            </>
                                        )}
                                    </Box>

                                    <Box
                                        className={`${classes.sectionCard} ${classes.sectionCardNotes} ${classes.sectionBeforeStory}`}
                                    >
                                        <Box className={classes.sectionHeader}>
                                            <Typography
                                                className={`${classes.sectionTitle} ${classes.sectionHeaderTitle}`}
                                            >
                                                <NotesIcon
                                                    className={
                                                        classes.sectionTitleIcon
                                                    }
                                                    style={{
                                                        color:
                                                            CARD_ACCENT.notes,
                                                    }}
                                                />
                                                Notes
                                            </Typography>
                                            {!readOnly && (
                                                <Button
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    startIcon={<AddIcon />}
                                                    className={
                                                        classes.addNoteButton
                                                    }
                                                    onClick={(event) =>
                                                        setNoteComposeAnchor(
                                                            event.currentTarget,
                                                        )
                                                    }
                                                >
                                                    Add Note
                                                </Button>
                                            )}
                                        </Box>
                                        {notesLoading ? (
                                            <CircularProgress size={18} />
                                        ) : notes.length === 0 ? (
                                            <Typography
                                                className={classes.empty}
                                            >
                                                No notes yet.
                                            </Typography>
                                        ) : (
                                            <Box className={classes.notesList}>
                                                {notes.map((note) => (
                                                    <Box
                                                        key={
                                                            note.source
                                                                ? `${note.source}-${note.source_id}`
                                                                : `staff-${note.id}`
                                                        }
                                                        className={
                                                            classes.noteCard
                                                        }
                                                    >
                                                        <Box
                                                            className={
                                                                classes.noteMetaRow
                                                            }
                                                        >
                                                            <Typography
                                                                className={
                                                                    classes.noteMeta
                                                                }
                                                                style={{
                                                                    marginBottom: 0,
                                                                }}
                                                            >
                                                                {note.created_by_name ||
                                                                    'Staff'}{' '}
                                                                ·{' '}
                                                                {formatDateTime(
                                                                    note.created_date,
                                                                )}
                                                            </Typography>
                                                            {note.source ===
                                                                'tour' && (
                                                                <Chip
                                                                    size="small"
                                                                    label="Tour"
                                                                    className={
                                                                        classes.noteSourceChip
                                                                    }
                                                                    style={{
                                                                        backgroundColor:
                                                                            'rgba(6, 182, 212, 0.12)',
                                                                        color:
                                                                            '#0e7490',
                                                                    }}
                                                                />
                                                            )}
                                                            {note.source ===
                                                                'call' && (
                                                                <Chip
                                                                    size="small"
                                                                    label="Call"
                                                                    className={
                                                                        classes.noteSourceChip
                                                                    }
                                                                    style={{
                                                                        backgroundColor:
                                                                            'rgba(139, 92, 246, 0.12)',
                                                                        color:
                                                                            '#6d28d9',
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                        <Typography
                                                            className={
                                                                classes.value
                                                            }
                                                            style={{
                                                                whiteSpace:
                                                                    'pre-wrap',
                                                            }}
                                                        >
                                                            {note.body}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                        <Popover
                                            open={Boolean(noteComposeAnchor)}
                                            anchorEl={noteComposeAnchor}
                                            onClose={closeNoteCompose}
                                            anchorOrigin={{
                                                vertical: 'bottom',
                                                horizontal: 'right',
                                            }}
                                            transformOrigin={{
                                                vertical: 'top',
                                                horizontal: 'right',
                                            }}
                                            PaperProps={{
                                                className:
                                                    classes.noteComposePaper,
                                            }}
                                        >
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                variant="outlined"
                                                placeholder="Add a note…"
                                                value={noteBody}
                                                onChange={(event) =>
                                                    setNoteBody(
                                                        event.target.value,
                                                    )
                                                }
                                            />
                                            <Box
                                                display="flex"
                                                justifyContent="flex-end"
                                                mt={1}
                                            >
                                                <Button
                                                    size="small"
                                                    color="primary"
                                                    variant="contained"
                                                    disabled={
                                                        isSaving ||
                                                        !noteBody.trim()
                                                    }
                                                    onClick={handleAddNote}
                                                >
                                                    Save note
                                                </Button>
                                            </Box>
                                        </Popover>
                                    </Box>

                                    <StorySection
                                        title="Preferences"
                                        icon={TuneIcon}
                                        summary={
                                            housingSummary || 'No preferences'
                                        }
                                        accent={STORY_ACCENT.housing}
                                        open={Boolean(openSections.housing)}
                                        onToggle={() =>
                                            toggleSection('housing')
                                        }
                                    >
                                        {!readOnly && (
                                            <Box
                                                className={classes.storyActions}
                                            >
                                                <Button
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    className={
                                                        classes.addNoteButton
                                                    }
                                                    onClick={() =>
                                                        setPreferencesOpen(true)
                                                    }
                                                >
                                                    Edit
                                                </Button>
                                            </Box>
                                        )}
                                        {!hasHousing && (
                                            <Typography
                                                className={classes.empty}
                                            >
                                                No preferences yet.
                                            </Typography>
                                        )}
                                        <Field
                                            label="Floor plan"
                                            value={floorPlan}
                                        />
                                        <Field label="Budget" value={budget} />
                                        <Field label="Move-in" value={moveIn} />
                                        <Field
                                            label="Move-out"
                                            value={moveOut}
                                        />
                                        <Field label="School" value={school} />
                                        <Field
                                            label="Premiums"
                                            value={premiums}
                                        />
                                        <Field
                                            label="Roommates"
                                            value={roommates}
                                        />
                                        <Field
                                            label="Requests"
                                            value={requests}
                                        />
                                        {customAnswers.length > 0 && (
                                            <>
                                                <button
                                                    type="button"
                                                    className={
                                                        classes.nestedToggle
                                                    }
                                                    onClick={() =>
                                                        setCustomQuestionsOpen(
                                                            (current) =>
                                                                !current,
                                                        )
                                                    }
                                                >
                                                    {customQuestionsOpen
                                                        ? 'Hide custom answers'
                                                        : `${
                                                              customAnswers.length
                                                          } custom answer${
                                                              customAnswers.length ===
                                                              1
                                                                  ? ''
                                                                  : 's'
                                                          }`}
                                                </button>
                                                {customQuestionsOpen &&
                                                    customAnswers.map(
                                                        (answer) => {
                                                            const question = lead.guest_card?.card_questions?.find(
                                                                (row) =>
                                                                    row
                                                                        .custom_question
                                                                        .id ===
                                                                    answer.guest_card_custom_question,
                                                            )
                                                            return (
                                                                <Field
                                                                    key={
                                                                        answer.id
                                                                    }
                                                                    label={
                                                                        question
                                                                            ?.custom_question
                                                                            .question ||
                                                                        'Custom question'
                                                                    }
                                                                    value={
                                                                        answer.answer
                                                                    }
                                                                />
                                                            )
                                                        },
                                                    )}
                                            </>
                                        )}
                                    </StorySection>

                                    <StorySection
                                        title="Pets"
                                        icon={PetsIcon}
                                        summary={petsSummary(petRows)}
                                        accent={STORY_ACCENT.pets}
                                        open={Boolean(openSections.pets)}
                                        onToggle={() => toggleSection('pets')}
                                    >
                                        {!readOnly && (
                                            <Box
                                                className={classes.storyActions}
                                            >
                                                {petRows.length > 0 && (
                                                    <Button
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        className={
                                                            classes.addNoteButton
                                                        }
                                                        onClick={() => {
                                                            setPetsStartWithNew(
                                                                false,
                                                            )
                                                            setPetsOpen(true)
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                )}
                                                <Button
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    startIcon={<AddIcon />}
                                                    className={
                                                        classes.addNoteButton
                                                    }
                                                    onClick={() => {
                                                        setPetsStartWithNew(
                                                            true,
                                                        )
                                                        setPetsOpen(true)
                                                    }}
                                                >
                                                    Add pet
                                                </Button>
                                            </Box>
                                        )}
                                        {petRows.length === 0 ? (
                                            <Typography
                                                className={classes.empty}
                                            >
                                                No pets on file.
                                            </Typography>
                                        ) : (
                                            petRows.map((pet, index) => (
                                                <PetDetails
                                                    key={`${pet.name}-${index}`}
                                                    pet={pet}
                                                />
                                            ))
                                        )}
                                    </StorySection>

                                    {hasApplication && (
                                        <StorySection
                                            title="Application"
                                            icon={AssignmentOutlinedIcon}
                                            summary={
                                                applicationSummary || 'Details'
                                            }
                                            accent={STORY_ACCENT.application}
                                            open={Boolean(
                                                openSections.application,
                                            )}
                                            onToggle={() =>
                                                toggleSection('application')
                                            }
                                        >
                                            <Field
                                                label="Qualifying"
                                                value={qualifying}
                                            />
                                            <Field
                                                label="Progress"
                                                value={progress}
                                            />
                                            <Field
                                                label="Guarantor"
                                                value={guarantor}
                                            />
                                            {lead.has_submission_pdf ? (
                                                <Box className={classes.row}>
                                                    <Typography
                                                        className={
                                                            classes.label
                                                        }
                                                    >
                                                        Application PDF
                                                    </Typography>
                                                    <OpenDocButton
                                                        onClick={
                                                            handleOpenApplicationPdf
                                                        }
                                                    />
                                                </Box>
                                            ) : null}
                                        </StorySection>
                                    )}

                                    {hasScreening && (
                                        <StorySection
                                            title="Screening"
                                            icon={VerifiedUserOutlinedIcon}
                                            summary={
                                                screeningSummary || 'Details'
                                            }
                                            accent={STORY_ACCENT.screening}
                                            open={Boolean(
                                                openSections.screening,
                                            )}
                                            onToggle={() =>
                                                toggleSection('screening')
                                            }
                                        >
                                            <Field
                                                label="Result"
                                                value={screeningLabel}
                                            />
                                            <Field
                                                label="Background"
                                                value={backgroundLabel}
                                            />
                                            <Field
                                                label="Credit"
                                                value={
                                                    lead.credit_score != null
                                                        ? String(
                                                              lead.credit_score,
                                                          )
                                                        : null
                                                }
                                            />
                                            {lead.screening_report_url ? (
                                                <Box className={classes.row}>
                                                    <Typography
                                                        className={
                                                            classes.label
                                                        }
                                                    >
                                                        Screening report
                                                    </Typography>
                                                    <OpenDocButton
                                                        href={
                                                            lead.screening_report_url
                                                        }
                                                    />
                                                </Box>
                                            ) : null}
                                            {!readOnly &&
                                                canScreen &&
                                                onStartScreening &&
                                                nextActionLabel !==
                                                    'Send screening' && (
                                                    <Button
                                                        size="small"
                                                        color="primary"
                                                        disabled={
                                                            isStartingScreening
                                                        }
                                                        onClick={() =>
                                                            onStartScreening(
                                                                lead.id,
                                                            )
                                                        }
                                                        style={{ marginTop: 8 }}
                                                    >
                                                        Send screening
                                                    </Button>
                                                )}
                                            {!readOnly &&
                                                canResend &&
                                                onResendScreeningInvitation && (
                                                    <Button
                                                        size="small"
                                                        disabled={
                                                            isResendingScreeningInvitation
                                                        }
                                                        onClick={() =>
                                                            onResendScreeningInvitation(
                                                                lead.id,
                                                            )
                                                        }
                                                        style={{ marginTop: 8 }}
                                                    >
                                                        Resend screening invite
                                                    </Button>
                                                )}
                                        </StorySection>
                                    )}

                                    <StorySection
                                        title="Tours"
                                        icon={CalendarTodayIcon}
                                        summary={tourSummary || 'No tours'}
                                        accent={STORY_ACCENT.tours}
                                        open={Boolean(openSections.tours)}
                                        onToggle={() => toggleSection('tours')}
                                    >
                                        {canScheduleTour && (
                                            <Box
                                                className={classes.storyActions}
                                            >
                                                {lead.current_tour && (
                                                    <Button
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        className={
                                                            classes.addNoteButton
                                                        }
                                                        onClick={() =>
                                                            onScheduleTour?.(
                                                                lead.id,
                                                                lead
                                                                    .current_tour
                                                                    ?.id,
                                                            )
                                                        }
                                                    >
                                                        Edit
                                                    </Button>
                                                )}
                                                <Button
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    startIcon={<AddIcon />}
                                                    className={
                                                        classes.addNoteButton
                                                    }
                                                    onClick={() =>
                                                        onScheduleTour?.(
                                                            lead.id,
                                                        )
                                                    }
                                                >
                                                    Add tour
                                                </Button>
                                            </Box>
                                        )}
                                        {!currentTour &&
                                        otherTours.length === 0 ? (
                                            <Typography
                                                className={classes.empty}
                                            >
                                                No tours yet.
                                            </Typography>
                                        ) : (
                                            <>
                                                {currentTour && (
                                                    <TourDetails
                                                        tour={currentTour}
                                                    />
                                                )}
                                                {otherTours.map((tour) => (
                                                    <TourDetails
                                                        key={tour.id}
                                                        tour={tour}
                                                    />
                                                ))}
                                            </>
                                        )}
                                    </StorySection>

                                    <StorySection
                                        title="Calls"
                                        icon={PhoneIcon}
                                        summary={callsSummary}
                                        accent={STORY_ACCENT.calls}
                                        open={callsOpen}
                                        onToggle={() => toggleSection('calls')}
                                    >
                                        {callsLoading ? (
                                            <CircularProgress size={18} />
                                        ) : calls.length === 0 ? (
                                            <Typography
                                                className={classes.empty}
                                            >
                                                No calls
                                            </Typography>
                                        ) : (
                                            calls.map((call) => (
                                                <Field
                                                    key={call.id}
                                                    label={
                                                        formatDateTime(
                                                            call.created_at,
                                                        ) ?? 'Call'
                                                    }
                                                    value={`${
                                                        call.duration_sec
                                                            ? `${Math.max(
                                                                  1,
                                                                  Math.round(
                                                                      call.duration_sec /
                                                                          60,
                                                                  ),
                                                              )} min`
                                                            : 'Call'
                                                    }${
                                                        call.call_topic
                                                            ? ` · ${call.call_topic}`
                                                            : ''
                                                    }`}
                                                />
                                            ))
                                        )}
                                    </StorySection>

                                    <PersonServiceRequestSection
                                        groups={serviceRequestGroups}
                                        requests={serviceRequests}
                                        loading={serviceRequestsLoading}
                                        hasResident={hasResident}
                                        open={Boolean(
                                            openSections.serviceRequests,
                                        )}
                                        onToggle={() =>
                                            toggleSection('serviceRequests')
                                        }
                                    />

                                    {documents.length > 0 && (
                                        <Box
                                            className={`${classes.section} ${classes.sectionCard} ${classes.sectionCardDocuments}`}
                                        >
                                            <Typography
                                                className={classes.sectionTitle}
                                            >
                                                <DescriptionOutlinedIcon
                                                    className={
                                                        classes.sectionTitleIcon
                                                    }
                                                    style={{
                                                        color:
                                                            CARD_ACCENT.documents,
                                                    }}
                                                />
                                                Documents
                                            </Typography>
                                            {documents.map((doc) => {
                                                const meta = [
                                                    doc.status,
                                                    formatDateTime(doc.at),
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')
                                                const canOpen =
                                                    doc.key ===
                                                        'application-pdf' ||
                                                    Boolean(doc.href)
                                                return (
                                                    <Box
                                                        key={doc.key}
                                                        className={
                                                            classes.documentRow
                                                        }
                                                    >
                                                        <Typography
                                                            className={
                                                                classes.documentName
                                                            }
                                                        >
                                                            {doc.name}
                                                        </Typography>
                                                        {meta ? (
                                                            <Typography
                                                                className={
                                                                    classes.documentMeta
                                                                }
                                                            >
                                                                {meta}
                                                            </Typography>
                                                        ) : null}
                                                        {canOpen ? (
                                                            <OpenDocButton
                                                                href={doc.href}
                                                                onClick={
                                                                    doc.key ===
                                                                    'application-pdf'
                                                                        ? handleOpenApplicationPdf
                                                                        : undefined
                                                                }
                                                            />
                                                        ) : null}
                                                    </Box>
                                                )
                                            })}
                                        </Box>
                                    )}
                                    <EditPreferencesDialog
                                        open={preferencesOpen}
                                        lead={lead}
                                        onClose={() =>
                                            setPreferencesOpen(false)
                                        }
                                    />
                                    <EditPetsDialog
                                        open={petsOpen}
                                        leadId={lead.id}
                                        pets={petRows}
                                        startWithNew={petsStartWithNew}
                                        onClose={() => {
                                            setPetsOpen(false)
                                            setPetsStartWithNew(false)
                                        }}
                                    />
                                </>
                            )}
                        </>
                    )}
                </Box>
            </Dialog>
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete this person?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary">
                        {deleteIsProtected
                            ? `${name} has a lease, account, or is further along in the pipeline. Deleting removes this season record, including notes, tours, and applications. This cannot be undone.`
                            : `Delete ${name} from this season? Notes, tours, and applications on this record will be removed. This cannot be undone.`}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteConfirmOpen(false)}
                        disabled={isDeletingLead}
                        style={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="secondary"
                        variant="contained"
                        disabled={!lead || isDeletingLead}
                        onClick={async () => {
                            if (!lead || !onDeleteLead) return
                            await onDeleteLead(lead.id)
                            setDeleteConfirmOpen(false)
                        }}
                        style={{ textTransform: 'none' }}
                    >
                        {isDeletingLead ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export const LeadRecordDrawer = PersonFileDialog

function PersonServiceRequestSection({
    groups,
    requests,
    loading,
    hasResident,
    open,
    onToggle,
}: {
    groups: PersonServiceRequestGroup[]
    requests: PersonServiceRequest[]
    loading: boolean
    hasResident: boolean
    open: boolean
    onToggle: () => void
}) {
    const classes = useStyles()
    return (
        <StorySection
            title="Service requests"
            icon={BuildIcon}
            summary={serviceRequestsSummary(requests, !loading, hasResident)}
            accent={STORY_ACCENT.serviceRequests}
            open={open}
            onToggle={onToggle}
        >
            {!hasResident ? (
                <Typography className={classes.empty}>
                    No service requests. These appear after they have a resident
                    account.
                </Typography>
            ) : loading ? (
                <CircularProgress size={18} />
            ) : requests.length === 0 ? (
                <Typography className={classes.empty}>
                    No service requests.
                </Typography>
            ) : (
                groups.map((group) => (
                    <Box key={group.leaseId} className={classes.leaseGroup}>
                        <Typography className={classes.leaseGroupTitle}>
                            {group.label}
                        </Typography>
                        {group.requests.map((row) => (
                            <Box key={row.id} className={classes.requestFact}>
                                <Typography
                                    className={classes.requestFactTitle}
                                >
                                    {row.issue_title ||
                                        row.description ||
                                        'Service request'}
                                </Typography>
                                <Typography className={classes.requestFactMeta}>
                                    {serviceRequestFacts(row)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                ))
            )}
        </StorySection>
    )
}

function StorySection({
    title,
    icon: Icon,
    summary,
    accent,
    open,
    onToggle,
    children,
}: {
    title: string
    icon: React.ComponentType<SvgIconProps>
    summary: string
    accent: string
    open: boolean
    onToggle: () => void
    children: React.ReactNode
}) {
    const classes = useStyles()
    const accentStyle = { borderLeft: `4px solid ${accent}` }
    return (
        <Box className={classes.section}>
            <button
                type="button"
                className={`${classes.storyToggle} ${
                    open ? classes.storyToggleOpen : ''
                }`}
                style={accentStyle}
                onClick={onToggle}
            >
                <Icon
                    className={classes.storyTitleIcon}
                    style={{ color: accent }}
                />
                <Typography className={classes.storyTitle}>{title}</Typography>
                <Typography className={classes.storySummary} noWrap>
                    {summary}
                </Typography>
                <ExpandMoreIcon
                    className={`${classes.storyChevron} ${
                        open ? classes.storyChevronOpen : ''
                    }`}
                />
            </button>
            {open ? (
                <Box className={classes.storyBody} style={accentStyle}>
                    {children}
                </Box>
            ) : null}
        </Box>
    )
}

function TourDetails({ tour }: { tour: LeadTour }) {
    const classes = useStyles()
    const meta = tourStatusMeta(tour.status)
    const StatusIcon = meta.Icon
    return (
        <Box className={classes.tourBlock}>
            <Box className={classes.tourIcon} style={{ color: meta.color }}>
                <StatusIcon />
            </Box>
            <Box className={classes.tourFields}>
                <Typography
                    className={classes.tourStatus}
                    style={{ color: meta.color }}
                >
                    {tourStatusLabel(tour.status)}
                </Typography>
                <Field label="When" value={formatDateTime(tour.starts_at)} />
                <Field
                    label="Given by"
                    value={tour.given_by?.name || 'Unassigned'}
                />
                <Field label="Notes" value={tour.notes || null} />
            </Box>
        </Box>
    )
}

function PetDetails({ pet }: { pet: LeasePet }) {
    const classes = useStyles()
    const [broken, setBroken] = useState(false)
    const pictureUrl = (pet.picture_url || '').trim()
    return (
        <Box className={classes.petBlock}>
            {pictureUrl && !broken ? (
                <img
                    src={pictureUrl}
                    alt={formatPetLabel(pet)}
                    className={classes.petPhoto}
                    onError={() => setBroken(true)}
                />
            ) : null}
            <Box className={classes.petFields}>
                <Field label="Pet" value={formatPetLabel(pet)} />
                <Field label="Breed" value={pet.breed || null} />
                <Field
                    label="Weight"
                    value={pet.weight ? `${pet.weight} lb` : null}
                />
                <Field label="Notes" value={pet.notes || null} />
            </Box>
        </Box>
    )
}

function OpenDocButton({
    href,
    onClick,
}: {
    href?: string
    onClick?: () => void
}) {
    const classes = useStyles()
    const shared = {
        size: 'small' as const,
        color: 'primary' as const,
        variant: 'outlined' as const,
        className: classes.addNoteButton,
        startIcon: <OpenInNewIcon />,
        onClick,
        children: 'Open',
    }
    if (href) {
        return (
            <Button
                {...shared}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
            />
        )
    }
    return <Button {...shared} />
}

function Field({
    label,
    value,
}: {
    label: string
    value: string | null | undefined
}) {
    const classes = useStyles()
    if (value == null || value === '') return null
    return (
        <Box className={classes.row}>
            <Typography className={classes.label}>{label}</Typography>
            <Typography className={classes.value}>{value}</Typography>
        </Box>
    )
}
